import {
	useCreateCustomerPortalSession,
	useDeleteProfilePicture,
	useIsFreeMembership,
	useMembershipInfo,
	useRequestPasswordReset,
	useUpdateUserProfile,
	useUploadProfilePicture,
	useUserProfile,
	useUserSubscription,
} from "@bodhistreams/api/src";
import { useRequestAccountDeletion } from "@bodhistreams/queries/src";
import { BodhiButton, BodhiLoader, bodhiMessage } from "@bodhistreams/ui";
import { theme as antdTheme } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountCard from "../components/profile/AccountCard";
import ProfileHeader from "../components/profile/ProfileHeader";
import SubscriptionCard from "../components/profile/SubscriptionCard";
import "../components/profile/profileLayout.css";
import PageLayout from "./PageLayout";

const Profile = () => {
	const navigate = useNavigate();
	const { token: antdToken } = antdTheme.useToken();
	const {
		data: user,
		isLoading: loading,
		refetch: refetchUserProfile,
	} = useUserProfile();
	const { data: userSubscription } = useUserSubscription(Boolean(user));
	const updateUserProfileMutation = useUpdateUserProfile();
	const requestPasswordResetMutation = useRequestPasswordReset();
	const createPortalSessionMutation = useCreateCustomerPortalSession();
	const uploadAvatarMutation = useUploadProfilePicture();
	const deleteAvatarMutation = useDeleteProfilePicture();
	const requestDeletionMutation = useRequestAccountDeletion();
	const isFreeMembership = useIsFreeMembership();
	const { membership, monthlyStreamHours } = useMembershipInfo();

	const [isEditing, setIsEditing] = useState(false);
	const [resetPasswordMessage, setResetPasswordMessage] = useState(null);
	const [confirmingDeletion, setConfirmingDeletion] = useState(false);
	const [editValues, setEditValues] = useState({
		first_name: "",
		last_name: "",
	});
	const [editErrors, setEditErrors] = useState({});

	useEffect(() => {
		if (!user) {
			refetchUserProfile();
		}
	}, [refetchUserProfile, user]);

	const startEditing = () => {
		setEditValues({
			first_name: user?.first_name || "",
			last_name: user?.last_name || "",
		});
		setEditErrors({});
		setIsEditing(true);
	};

	const handleChangeEdit = (field, value) =>
		setEditValues((v) => ({ ...v, [field]: value }));

	const handleSaveProfile = async () => {
		const errors = {};
		if (!editValues.first_name.trim())
			errors.first_name = "Please enter your first name";
		if (!editValues.last_name.trim())
			errors.last_name = "Please enter your last name";
		setEditErrors(errors);
		if (Object.keys(errors).length > 0) return;

		try {
			await updateUserProfileMutation.mutateAsync(editValues);
			setIsEditing(false);
			bodhiMessage.success("Profile updated successfully");
		} catch (_error) {
			bodhiMessage.error("Failed to update profile");
		}
	};

	const handleManageSubscription = async () => {
		if (!userSubscription?.stripe_subscription_id) {
			navigate("/pricing");
			return;
		}

		try {
			const response = await createPortalSessionMutation.mutateAsync();
			if (response?.portal_url) {
				window.location.href = response.portal_url;
			} else {
				bodhiMessage.error("Failed to get subscription management URL");
			}
		} catch (error) {
			console.error("Failed to create portal session:", error);
			bodhiMessage.error(
				error.response?.data?.error || "Failed to open subscription management",
			);
		}
	};

	const handleResetPassword = async () => {
		try {
			const response = await requestPasswordResetMutation.mutateAsync(
				user?.email,
			);
			setResetPasswordMessage(
				response.message ||
					"Password reset email sent! Please check your inbox and follow the link.",
			);
		} catch (_error) {
			setResetPasswordMessage(null);
			bodhiMessage.error("Failed to send password reset email");
		}
	};

	const handleAvatarChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		try {
			await uploadAvatarMutation.mutateAsync(file);
			bodhiMessage.success("Profile picture updated");
		} catch (_err) {
			bodhiMessage.error("Failed to upload profile picture");
		}
	};

	const handleDeleteAvatar = async () => {
		try {
			await deleteAvatarMutation.mutateAsync();
			bodhiMessage.success("Profile picture removed");
		} catch (_err) {
			bodhiMessage.error("Failed to remove profile picture");
		}
	};

	// Two-step confirm: first click arms the button, second click sends the
	// deletion email. The account is only deleted via the link in that email.
	const handleRequestAccountDeletion = async () => {
		if (!confirmingDeletion) {
			setConfirmingDeletion(true);
			return;
		}
		try {
			await requestDeletionMutation.mutateAsync();
			bodhiMessage.success(
				"Deletion email sent — check your inbox and confirm via the link. It expires in 24 hours.",
			);
		} catch (error) {
			bodhiMessage.error(
				error.response?.data?.error || "Failed to send deletion email",
			);
		} finally {
			setConfirmingDeletion(false);
		}
	};

	if (loading) {
		return (
			<PageLayout>
				<div className="profile-loading">
					<BodhiLoader mode="inline" size={64} backgroundOpacity={0} />
				</div>
			</PageLayout>
		);
	}

	const stripeDetails = {
		price: userSubscription?.price ? parseFloat(userSubscription.price) : 0,
		billing_period: userSubscription?.billing_period || "monthly",
		product_name: userSubscription?.membership_name || membership?.name,
		cancel_at_period_end: userSubscription?.cancel_at_period_end || false,
		current_period_end: userSubscription?.current_period_end || null,
	};
	const canAccessCertificate =
		!isFreeMembership &&
		Boolean(userSubscription?.stripe_subscription_id || membership?.name);
	const streamingLimit = membership?.streaming_hours;
	const hasStreamingLimit =
		streamingLimit !== null && streamingLimit !== undefined;
	const usagePercentage = hasStreamingLimit
		? Math.min((monthlyStreamHours / streamingLimit) * 100, 100)
		: 0;
	const isOverLimit = hasStreamingLimit && monthlyStreamHours >= streamingLimit;
	const isManagingSubscription = createPortalSessionMutation.isPending;
	const devices = membership?.max_simultaneous_devices || 0;

	return (
		<PageLayout
			fullWidthContent={
				<div className="profile-page">
					<div className="profile-inner">
						<ProfileHeader
							user={user}
							canAccessCertificate={canAccessCertificate}
							isUploadingAvatar={uploadAvatarMutation.isPending}
							isDeletingAvatar={deleteAvatarMutation.isPending}
							isManagingSubscription={isManagingSubscription}
							onAvatarChange={handleAvatarChange}
							onDeleteAvatar={handleDeleteAvatar}
							onOpenCertificate={() => navigate("/certificate")}
							onEdit={startEditing}
							onManageSubscription={handleManageSubscription}
						/>

						<div className="profile-grid">
							<AccountCard
								user={user}
								isEditing={isEditing}
								editValues={editValues}
								editErrors={editErrors}
								onChangeEdit={handleChangeEdit}
								onCancelEdit={() => setIsEditing(false)}
								onSave={handleSaveProfile}
								isSaving={updateUserProfileMutation.isPending}
								onResetPassword={handleResetPassword}
								isResettingPassword={requestPasswordResetMutation.isPending}
								resetPasswordMessage={resetPasswordMessage}
							/>
							<SubscriptionCard
								stripeDetails={stripeDetails}
								hasStreamingLimit={hasStreamingLimit}
								streamingLimit={streamingLimit}
								monthlyStreamHours={monthlyStreamHours}
								usagePercentage={usagePercentage}
								isOverLimit={isOverLimit}
								devices={devices}
								isManagingSubscription={isManagingSubscription}
								onManageSubscription={handleManageSubscription}
							/>
						</div>

						<div className="profile-card bodhi-card">
							<div className="profile-card-head">
								<h2 className="profile-card-title">Danger zone</h2>
							</div>
							<p style={{ color: antdToken.colorTextSecondary }}>
								Permanently delete your account and all associated data. We
								email you a confirmation link first — deletion only happens
								after you confirm from that email, and it{" "}
								<strong style={{ color: antdToken.colorError }}>
									cannot be undone
								</strong>
								. See{" "}
								<a
									href="/delete-account"
									style={{ color: antdToken.colorPrimary }}
								>
									what gets deleted
								</a>
								.
							</p>
							<BodhiButton
								variant="danger"
								onClick={handleRequestAccountDeletion}
								loading={requestDeletionMutation.isPending}
							>
								{confirmingDeletion
									? "Send the deletion email"
									: "Delete account"}
							</BodhiButton>
						</div>
					</div>
				</div>
			}
		/>
	);
};

export default Profile;
